/**
 * Recipe Storage Service
 * Handles local storage and retrieval of recipe details with user preferences
 */

import { RecipeDetailRequest, RecipeDetailResponse, StoredRecipe, RecipeCacheKey } from '../types/recipe'
import { clientLogger } from '../utils/clientLogger'

export interface MealPlanContext {
  mealPlanId: string
  recipeName: string
  mealType: string
  positionInPlan: number
}

class RecipeStorageService {
  private storageKey = 'talktoast_recipe_cache'
  private maxCacheAge = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds
  private maxCacheSize = 100 // Maximum number of recipes to store

  /**
   * Generate a cache key from recipe request parameters
   */
  private generateCacheKey(request: RecipeDetailRequest): string {
    const keyData: RecipeCacheKey = {
      recipeName: request.recipeName.toLowerCase().trim(),
      mealType: request.mealType,
      servingSize: request.servingSize,
      dietaryPreference: request.userPreferences.dietaryPreference,
      dietaryRestrictions: request.userPreferences.dietaryRestrictions.sort(),
      unitsSystem: request.userPreferences.unitsSystem,
      measurementBias: request.userPreferences.measurementBias,
      appliances: request.userPreferences.availableAppliances.sort()
    }
    
    // Create a hash-like key from the normalized data
    const keyString = JSON.stringify(keyData)
    return btoa(keyString).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32)
  }

  /**
   * Get all cached recipes from localStorage
   */
  private getCachedRecipes(): Map<string, StoredRecipe> {
    try {
      const cached = localStorage.getItem(this.storageKey)
      if (!cached) return new Map()
      
      const data = JSON.parse(cached)
      const recipes = new Map<string, StoredRecipe>()
      
      // Convert to Map and filter out expired entries
      Object.entries(data).forEach(([key, value]) => {
        const storedRecipe = value as StoredRecipe
        const retrievedAt = new Date(storedRecipe.retrievedAt).getTime()
        const isExpired = Date.now() - retrievedAt > this.maxCacheAge
        
        if (!isExpired) {
          recipes.set(key, storedRecipe)
        }
      })
      
      return recipes
    } catch (error) {
      clientLogger.warn({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error loading cached recipes')
      return new Map()
    }
  }

  /**
   * Save cached recipes to localStorage
   */
  private saveCachedRecipes(recipes: Map<string, StoredRecipe>): void {
    try {
      // Convert Map to object and limit cache size
      const entries = Array.from(recipes.entries())
      
      // Sort by retrievedAt (newest first) and take only the most recent entries
      entries.sort(([,a], [,b]) => 
        new Date(b.retrievedAt).getTime() - new Date(a.retrievedAt).getTime()
      )
      
      const limitedEntries = entries.slice(0, this.maxCacheSize)
      const dataObject = Object.fromEntries(limitedEntries)
      
      localStorage.setItem(this.storageKey, JSON.stringify(dataObject))
      
      clientLogger.debug({ count: limitedEntries.length }, 'Saved recipes to cache')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error saving cached recipes')
    }
  }

  /**
   * Check if a recipe is cached and fresh
   */
  isRecipeCached(request: RecipeDetailRequest): boolean {
    const cacheKey = this.generateCacheKey(request)
    const cachedRecipes = this.getCachedRecipes()
    return cachedRecipes.has(cacheKey)
  }

  /**
   * Get a cached recipe if available
   */
  getCachedRecipe(request: RecipeDetailRequest): RecipeDetailResponse | null {
    const cacheKey = this.generateCacheKey(request)
    const cachedRecipes = this.getCachedRecipes()
    const storedRecipe = cachedRecipes.get(cacheKey)
    
    if (!storedRecipe) {
      clientLogger.debug({ recipeName: request.recipeName }, 'No cached recipe found')
      return null
    }

    clientLogger.debug({ recipeName: request.recipeName, cacheKey, retrievedAt: storedRecipe.retrievedAt, storedName: storedRecipe.recipe.name }, 'Retrieved cached recipe')
    
    return {
      recipe: storedRecipe.recipe,
      sessionId: request.conversationContext.sessionId,
      kitchenTools: storedRecipe.kitchenTools,
      cookingTips: storedRecipe.cookingTips
    }
  }

  /**
   * Store a recipe in the cache
   */
  storeRecipe(request: RecipeDetailRequest, response: RecipeDetailResponse): void {
    try {
      const cacheKey = this.generateCacheKey(request)
      const cachedRecipes = this.getCachedRecipes()
      
      const storedRecipe: StoredRecipe = {
        recipe: response.recipe,
        kitchenTools: response.kitchenTools,
        cookingTips: response.cookingTips,
        userPreferences: request.userPreferences,
        retrievedAt: new Date().toISOString(),
        cacheKey
      }
      
      cachedRecipes.set(cacheKey, storedRecipe)
      this.saveCachedRecipes(cachedRecipes)
      
      clientLogger.debug({ recipeName: request.recipeName, cacheKey, storedName: response.recipe.name, ingredientsCount: response.recipe.ingredients.length }, 'Stored recipe')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error storing recipe')
    }
  }

  /**
   * Clear all cached recipes
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.storageKey)
      clientLogger.debug({}, 'Cache cleared')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error clearing cache')
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { count: number; oldestEntry: string | null; newestEntry: string | null; totalSize: number } {
    const cachedRecipes = this.getCachedRecipes()
    const entries = Array.from(cachedRecipes.values())
    
    if (entries.length === 0) {
      return { count: 0, oldestEntry: null, newestEntry: null, totalSize: 0 }
    }
    
    const dates = entries.map(recipe => new Date(recipe.retrievedAt).getTime())
    const oldestEntry = new Date(Math.min(...dates)).toISOString()
    const newestEntry = new Date(Math.max(...dates)).toISOString()
    
    let totalSize = 0
    try {
      const cached = localStorage.getItem(this.storageKey)
      totalSize = cached ? cached.length : 0
    } catch (error) {
      // Ignore size calculation errors
    }
    
    return {
      count: entries.length,
      oldestEntry,
      newestEntry,
      totalSize
    }
  }

  /**
   * Remove expired recipes from cache
   */
  cleanupExpiredRecipes(): void {
    const cachedRecipes = this.getCachedRecipes() // This already filters expired
    this.saveCachedRecipes(cachedRecipes)
    clientLogger.debug({}, 'Cleaned up expired recipes')
  }

  /**
   * Get all cached recipe names for debugging
   */
  getCachedRecipeNames(): string[] {
    const cachedRecipes = this.getCachedRecipes()
    return Array.from(cachedRecipes.values()).map(stored => stored.recipe.name)
  }

  /**
   * Invalidate cache for a specific recipe (used when recipe gets rerolled)
   */
  invalidateRecipe(recipeName: string, _userPreferences: RecipeDetailRequest['userPreferences']): void {
    try {
      const cachedRecipes = this.getCachedRecipes()
      
      // Find all cache entries for this recipe name (may have multiple variants)
      const keysToRemove: string[] = []
      
      cachedRecipes.forEach((storedRecipe, cacheKey) => {
        if (storedRecipe.recipe.name.toLowerCase().trim() === recipeName.toLowerCase().trim()) {
          keysToRemove.push(cacheKey)
        }
      })
      
      // Remove the matching entries
      keysToRemove.forEach(key => cachedRecipes.delete(key))
      
      if (keysToRemove.length > 0) {
        this.saveCachedRecipes(cachedRecipes)
        clientLogger.debug({ count: keysToRemove.length, recipeName }, 'Invalidated cache entries for recipe')
      }
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error invalidating recipe cache')
    }
  }

  /**
   * Store recipe with meal plan context for better organization
   */
  storeRecipeWithContext(
    request: RecipeDetailRequest, 
    response: RecipeDetailResponse, 
    mealPlanContext?: MealPlanContext
  ): void {
    try {
      const cacheKey = this.generateCacheKey(request)
      const cachedRecipes = this.getCachedRecipes()
      
      const storedRecipe: StoredRecipe = {
        recipe: response.recipe,
        kitchenTools: response.kitchenTools,
        cookingTips: response.cookingTips,
        userPreferences: request.userPreferences,
        retrievedAt: new Date().toISOString(),
        cacheKey
      }
      
      cachedRecipes.set(cacheKey, storedRecipe)
      this.saveCachedRecipes(cachedRecipes)
      
      clientLogger.debug({ recipeName: request.recipeName, cacheKey, mealPlanId: mealPlanContext?.mealPlanId, storedName: response.recipe.name, ingredientsCount: response.recipe.ingredients.length }, 'Stored recipe with context')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error storing recipe with context')
    }
  }

  /**
   * Batch fetch multiple recipes for a meal plan (eager loading)
   */
  async batchFetchRecipes(
    requests: RecipeDetailRequest[],
    onProgress?: (completed: number, total: number, recipeName: string) => void
  ): Promise<Map<string, RecipeDetailResponse>> {
    const results = new Map<string, RecipeDetailResponse>()
    
    clientLogger.debug({ count: requests.length }, 'Starting batch fetch')
    
    for (let i = 0; i < requests.length; i++) {
      const request = requests[i]
      
      try {
        if (!request) continue

        // Check cache first
        const cached = this.getCachedRecipe(request)

        if (cached) {
          results.set(request.recipeName, cached)
          clientLogger.debug({ recipeName: request.recipeName }, 'Using cached recipe')
        } else {
          // Will be handled by AnthropicService which calls back to store
          clientLogger.debug({ recipeName: request.recipeName }, 'Recipe needs fetching')
        }

        if (onProgress) {
          onProgress(i + 1, requests.length, request.recipeName)
        }
      } catch (error) {
        clientLogger.error({ recipeName: request?.recipeName, err: error instanceof Error ? error : new Error(String(error)) }, 'Error processing recipe')
      }
    }
    
    return results
  }
}

export const recipeStorageService = new RecipeStorageService()
export default recipeStorageService